import React, { useState } from 'react';
import MarketingNav from '../components/layout/MarketingNav';
import { Button, Card, Badge } from '../components/ui';
import { Users, TrendingUp, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const ClinicianPreview = () => {
  const [activeView, setActiveView] = useState('roster');
  const [selectedClient, setSelectedClient] = useState(null);

  // Mock clinician data
  const mockClinicianStats = {
    totalClients: 12,
    activeToday: 8,
    completionRate: 87,
    averageMood: 6.8
  };

  const mockClinicianProfile = {
    firstName: 'Avery',
    lastName: 'Coleman',
    email: 'avery.coleman@mhdfmomentum.com',
    licenseType: 'LPC-S',
    licenseNumber: 'TX-482193',
    licenseState: 'TX',
    licenseExpiration: '2027-06-30'
  };

  // Mock client roster
  const mockClients = [
    {
      id: 1,
      firstName: 'Sarah',
      lastName: 'Mitchell',
      enrollmentDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
      currentDay: 45,
      currentPhase: 2,
      status: 'active',
      todayCompletion: 3,
      moodToday: 8,
      lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000),
      completionRate: 92,
      riskLevel: 'low'
    },
    {
      id: 2,
      firstName: 'James',
      lastName: 'Rodriguez',
      enrollmentDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000),
      currentDay: 120,
      currentPhase: 4,
      status: 'active',
      todayCompletion: 2,
      moodToday: 5,
      lastActivity: new Date(Date.now() - 8 * 60 * 60 * 1000),
      completionRate: 78,
      riskLevel: 'medium'
    },
    {
      id: 3,
      firstName: 'Maya',
      lastName: 'Chen',
      enrollmentDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      currentDay: 90,
      currentPhase: 3,
      status: 'active',
      todayCompletion: 3,
      moodToday: 7,
      lastActivity: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      completionRate: 95,
      riskLevel: 'low'
    },
    {
      id: 4,
      firstName: 'Marcus',
      lastName: 'Johnson',
      enrollmentDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      currentDay: 30,
      currentPhase: 1,
      status: 'at-risk',
      todayCompletion: 1,
      moodToday: 3,
      lastActivity: new Date(Date.now() - 36 * 60 * 60 * 1000),
      completionRate: 65,
      riskLevel: 'high'
    },
    {
      id: 5,
      firstName: 'Lisa',
      lastName: 'Thompson',
      enrollmentDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
      currentDay: 180,
      currentPhase: 4,
      status: 'graduated',
      todayCompletion: 0,
      moodToday: 9,
      lastActivity: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      completionRate: 98,
      riskLevel: 'low'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 border-emerald-200';
      case 'at-risk':
        return 'bg-amber-50 border-amber-200';
      case 'graduated':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-neutral-50 border-neutral-200';
    }
  };

  const getRiskBadge = (level) => {
    switch (level) {
      case 'low':
        return <Badge variant="success" size="sm">Low Risk</Badge>;
      case 'medium':
        return <Badge variant="warning" size="sm">Medium Risk</Badge>;
      case 'high':
        return <Badge variant="error" size="sm">High Risk</Badge>;
      default:
        return null;
    }
  };

  const formatLastActivity = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60));
    
    if (diff < 60) return `${diff}m ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Mock client details
  const selectedClientData = mockClients.find(c => c.id === selectedClient);

  return (
    <>
      <MarketingNav transparent={false} />
      <main className="mx-auto max-w-6xl px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <Badge variant="primary" size="sm">Clinician Preview</Badge>
          <h1 className="mt-3 text-4xl font-display font-semibold text-neutral-900">
            Clinician Dashboard
          </h1>
          <p className="mt-2 text-lg text-neutral-600">
            Manage your client roster and monitor mental wellness progress in real-time
          </p>
        </div>

        <Card className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm text-neutral-500">Clinician Profile</p>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                {mockClinicianProfile.firstName} {mockClinicianProfile.lastName}
              </p>
              <p className="text-sm text-neutral-600">{mockClinicianProfile.email}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">License Type</p>
                <p className="text-sm font-semibold text-neutral-900">{mockClinicianProfile.licenseType}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">License Number</p>
                <p className="text-sm font-semibold text-neutral-900">{mockClinicianProfile.licenseNumber}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">License State</p>
                <p className="text-sm font-semibold text-neutral-900">{mockClinicianProfile.licenseState}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">Expiration</p>
                <p className="text-sm font-semibold text-neutral-900">
                  {new Date(mockClinicianProfile.licenseExpiration).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Tab Navigation */}
        <div className="mb-6 flex gap-2 border-b border-neutral-200 overflow-x-auto">
          {[
            { id: 'roster', label: 'Client Roster', icon: '👥' },
            { id: 'monitoring', label: 'Monitoring', icon: '📊' },
            { id: 'metrics', label: 'Metrics', icon: '📈' },
            { id: 'alerts', label: 'Alerts & Actions', icon: '⚠️' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`px-4 py-3 font-medium whitespace-nowrap ${
                activeView === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Roster View */}
        {activeView === 'roster' && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Active Clients</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockClinicianStats.totalClients}</p>
                  </div>
                  <Users className="h-8 w-8 text-primary-600" />
                </div>
              </Card>

              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Active Today</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockClinicianStats.activeToday}</p>
                  </div>
                  <Clock className="h-8 w-8 text-emerald-600" />
                </div>
              </Card>

              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Avg. Completion</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockClinicianStats.completionRate}%</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-blue-600" />
                </div>
              </Card>

              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Avg. Mood</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockClinicianStats.averageMood}/10</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-rose-600" />
                </div>
              </Card>
            </div>

            {/* Client List */}
            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Your Clients</h3>
              
              <div className="space-y-3">
                {mockClients.map(client => (
                  <div
                    key={client.id}
                    onClick={() => setSelectedClient(client.id)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedClient === client.id
                        ? 'border-primary-600 bg-primary-50'
                        : `${getStatusColor(client.status)} hover:border-primary-600`
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-neutral-900">
                          {client.firstName} {client.lastName}
                        </h4>
                        <p className="text-sm text-neutral-600">
                          Day {client.currentDay} • Phase {client.currentPhase} • 
                          {' '}{client.currentDay} login days
                        </p>
                      </div>
                      <div className="text-right">
                        {getRiskBadge(client.riskLevel)}
                        <p className="text-sm text-neutral-600 mt-2">{client.completionRate}% complete</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div>
                          <p className="text-xs text-neutral-600">Today</p>
                          <p className="font-semibold text-neutral-900">{client.todayCompletion}/3</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-600">Mood</p>
                          <p className="font-semibold text-neutral-900">{client.moodToday}/10</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-600">Last Activity</p>
                          <p className="font-semibold text-neutral-900">{formatLastActivity(client.lastActivity)}</p>
                        </div>
                      </div>
                      <div className="h-8 w-8 bg-neutral-200 rounded-full flex items-center justify-center">
                        <ChevronRight className="h-5 w-5 text-neutral-600" />
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3 h-2 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-600"
                        style={{ width: `${client.completionRate}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Selected Client Details */}
            {selectedClientData && (
              <Card>
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                  {selectedClientData.firstName}'s Detailed View
                </h3>

                <div className="grid gap-6 md:grid-cols-2">
                  {/* Progress Overview */}
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-3">Progress Overview</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm text-neutral-600">Wellness Progress</p>
                          <p className="font-semibold text-neutral-900">
                            {selectedClientData.currentDay}/180 days
                          </p>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-600"
                            style={{ width: `${(selectedClientData.currentDay / 180) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm text-neutral-600">Completion Rate</p>
                          <p className="font-semibold text-neutral-900">{selectedClientData.completionRate}%</p>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-600"
                            style={{ width: `${selectedClientData.completionRate}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-3">Quick Actions</h4>
                    <div className="space-y-2">
                      <Button variant="secondary" className="w-full text-left">
                        📝 View Journal Entries
                      </Button>
                      <Button variant="secondary" className="w-full text-left">
                        📊 Detailed Metrics
                      </Button>
                      <Button variant="secondary" className="w-full text-left">
                        💬 Send Message
                      </Button>
                      <Button variant="secondary" className="w-full text-left">
                        ⚠️ Flag for Review
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Monitoring View */}
        {activeView === 'monitoring' && (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Today's Activity</h3>
              
              <div className="space-y-3">
                {mockClients
                  .filter(c => c.todayCompletion > 0)
                  .map(client => (
                    <div key={client.id} className="flex items-center justify-between p-4 rounded-lg border border-neutral-200 hover:bg-neutral-50">
                      <div className="flex-1">
                        <p className="font-semibold text-neutral-900">{client.firstName} {client.lastName}</p>
                        <p className="text-sm text-neutral-600">Completed {client.todayCompletion} of 3 pillars • Mood: {client.moodToday}/10</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-emerald-600 font-medium">Active {formatLastActivity(client.lastActivity)}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Needs Attention</h3>
              
              <div className="space-y-3">
                {mockClients
                  .filter(c => c.status === 'at-risk' || c.completionRate < 70)
                  .map(client => (
                    <div key={client.id} className="flex items-center justify-between p-4 rounded-lg border-2 border-amber-200 bg-amber-50">
                      <div className="flex items-start gap-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-neutral-900">{client.firstName} {client.lastName}</p>
                          <p className="text-sm text-neutral-600">
                            {client.status === 'at-risk' && 'Risk level elevated'}
                            {client.completionRate < 70 && `Low completion rate: ${client.completionRate}%`}
                          </p>
                        </div>
                      </div>
                      <Button variant="secondary" size="sm">Review</Button>
                    </div>
                  ))}
              </div>
            </Card>
          </div>
        )}

        {/* Metrics View */}
        {activeView === 'metrics' && (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-6">Roster Metrics</h3>
              
              <div className="grid gap-6 md:grid-cols-2">
                {/* Completion Distribution */}
                <div>
                  <h4 className="font-semibold text-neutral-900 mb-4">Completion Rates</h4>
                  <div className="space-y-3">
                    {[
                      { label: '90-100%', count: 3, color: 'bg-emerald-600' },
                      { label: '80-89%', count: 5, color: 'bg-blue-600' },
                      { label: '70-79%', count: 3, color: 'bg-amber-600' },
                      { label: 'Below 70%', count: 1, color: 'bg-rose-600' }
                    ].map((stat, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm text-neutral-600">{stat.label}</p>
                          <p className="font-semibold text-neutral-900">{stat.count} clients</p>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className={stat.color}
                            style={{ width: `${(stat.count / 12) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status Distribution */}
                <div>
                  <h4 className="font-semibold text-neutral-900 mb-4">Client Status</h4>
                  <div className="space-y-3">
                    {[
                      { label: 'Active', count: 8, color: 'emerald' },
                      { label: 'At Risk', count: 2, color: 'amber' },
                      { label: 'Graduated', count: 2, color: 'blue' }
                    ].map((stat, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-neutral-50">
                        <div className="flex items-center gap-2">
                          <div className={`h-3 w-3 rounded-full bg-${stat.color}-600`} />
                          <p className="text-neutral-900 font-medium">{stat.label}</p>
                        </div>
                        <p className="font-semibold text-neutral-900">{stat.count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Mood Trend (Last 30 Days)</h3>
              <div className="h-40 flex items-end gap-1 bg-neutral-50 rounded-lg p-4">
                {Array.from({ length: 30 }).map((_, i) => {
                  const moodValue = 5 + Math.sin(i / 3) * 2 + Math.random() * 2;
                  return (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-gradient-to-t from-primary-600 to-primary-400"
                      style={{ height: `${(moodValue / 10) * 100}%`, minHeight: '2px' }}
                      title={`Day ${i + 1}: ${moodValue.toFixed(1)}/10`}
                    />
                  );
                })}
              </div>
              <p className="text-sm text-neutral-600 mt-4">
                Average mood across roster: <span className="font-semibold text-neutral-900">6.8/10</span>
              </p>
            </Card>
          </div>
        )}

        {/* Alerts & Actions View */}
        {activeView === 'alerts' && (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">System Alerts</h3>
              
              <div className="space-y-3">
                {[
                  {
                    severity: 'high',
                    client: 'Marcus Johnson',
                    message: 'No activity for 36 hours. Mood declining.',
                    action: 'Check in'
                  },
                  {
                    severity: 'medium',
                    client: 'James Rodriguez',
                    message: 'Completion rate below threshold (78%)',
                    action: 'Review'
                  },
                  {
                    severity: 'low',
                    client: 'Sarah Mitchell',
                    message: 'Approaching day 90 milestone',
                    action: 'Celebrate'
                  }
                ].map((alert, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-2 ${
                      alert.severity === 'high'
                        ? 'border-rose-200 bg-rose-50'
                        : alert.severity === 'medium'
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-blue-200 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-neutral-900">{alert.client}</p>
                        <p className="text-sm text-neutral-700 mt-1">{alert.message}</p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="flex-shrink-0 ml-4"
                      >
                        {alert.action}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Available Actions</h3>
              
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  { icon: '💬', label: 'Send Message', description: 'Reach out to a client' },
                  { icon: '⚠️', label: 'Flag for Review', description: 'Mark client for attention' },
                  { icon: '📊', label: 'Export Metrics', description: 'Download client data' },
                  { icon: '🎓', label: 'Graduation Review', description: 'Prepare graduation milestone' },
                  { icon: '⏸️', label: 'Suspend Program', description: 'Temporarily pause progress' },
                  { icon: '📝', label: 'Add Clinical Note', description: 'Document observations' }
                ].map((action, idx) => (
                  <Button
                    key={idx}
                    variant="secondary"
                    className="h-auto justify-start p-4 text-left"
                  >
                    <div>
                      <p className="text-lg mb-1">{action.icon}</p>
                      <p className="font-semibold text-neutral-900">{action.label}</p>
                      <p className="text-xs text-neutral-600">{action.description}</p>
                    </div>
                  </Button>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 border-t border-neutral-200 pt-8">
          <Card className="bg-primary-50">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">Empower Your Clients</h3>
              <p className="text-neutral-600 mb-6">
                MHDF Momentum gives clinicians comprehensive tools to monitor and support mental wellness
              </p>
              <Button onClick={() => window.location.href = '/login'}>Access Your Dashboard</Button>
            </div>
          </Card>
        </div>
      </main>
    </>
  );
};

// Missing import helper
const ChevronRight = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export default ClinicianPreview;
